import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  InputLabel,
  FormControl,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  TextField,
} from "@mui/material";
import api from "../services/api";
import { usePreferences } from "../contexts/PreferencesContext";
import LinkIcon from "@mui/icons-material/Link";

const currentMonth = new Date().getMonth() + 1; // 1-indexed
const currentYear = new Date().getFullYear();

export default function BankTransactions() {
  const { bankConnected, plan, preferencesLoaded, refreshPreferences } =
    usePreferences();
  const isProPlus = plan === "proplus";

  const [selectedBank, setSelectedBank] = useState("");
  const [institutions, setInstitutions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [filteredTxs, setFilteredTxs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [month, setMonth] = useState(currentMonth);
  const [year, setYear] = useState(currentYear);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!bankConnected) {
      api
        .get("/bank/institutions")
        .then((res) => setInstitutions(res.data))
        .catch((err) => console.error("Failed to load institutions", err));
    }
  }, [bankConnected]);

  useEffect(() => {
    refreshPreferences?.();
  }, []);

  useEffect(() => {
    if (bankConnected && month && year) fetchTransactions();
  }, [bankConnected, month, year]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const res = await api.get("/bank/transactions", {
        params: { month, year },
      });

      const monthString = String(month).padStart(2, "0");
      const selectedPrefix = `${year}-${monthString}`;

      const txs = (res.data.transactions || [])
        .filter((tx) => tx.date?.startsWith(selectedPrefix))
        .sort((a, b) => new Date(b.date) - new Date(a.date));

      setTransactions(txs);
      setFilteredTxs(txs);
    } catch (err) {
      console.error("Failed to load transactions", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    const filtered = transactions.filter((tx) =>
      tx.description.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredTxs(filtered);
  }, [search, transactions]);

  const handleConnectBank = async () => {
    if (!selectedBank) {
      return setSnackbar({
        open: true,
        message: "Please select a bank",
        severity: "warning",
      });
    }

    try {
      const res = await api.post("/bank/connect", {
        institutionId: selectedBank,
        redirectTo: "/dashboard/bank-transactions",
      });
      window.location.href = res.data.link;
    } catch (err) {
      console.error("Connect error", err);
      setSnackbar({
        open: true,
        message: "Failed to initiate connection",
        severity: "error",
      });
    }
  };

  const formatDateWithSuffix = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const suffix =
      day % 10 === 1 && day !== 11
        ? "st"
        : day % 10 === 2 && day !== 12
        ? "nd"
        : day % 10 === 3 && day !== 13
        ? "rd"
        : "th";

    const formatted = date.toLocaleDateString("en-GB", {
      month: "long",
      year: "numeric",
    });

    return `${day}${suffix} ${formatted}`;
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        🏦 Bank Transactions
      </Typography>

      {!preferencesLoaded ? (
        <Box textAlign="center" p={4}>
          <CircularProgress />
        </Box>
      ) : !bankConnected ? (
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 4,
            textAlign: "center",
            maxWidth: 500,
            mx: "auto",
          }}
        >
          <Typography variant="h5" gutterBottom>
            🏦 Connect Your Bank
          </Typography>
          <Typography sx={{ mb: 3 }}>
            Securely sync bank transactions
          </Typography>

          {isProPlus ? (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select your bank</InputLabel>
                <Select
                  value={selectedBank}
                  onChange={(e) => setSelectedBank(e.target.value)}
                >
                  {institutions.map((inst) => (
                    <MenuItem key={inst.id} value={inst.id}>
                      {inst.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                fullWidth
                disabled={!selectedBank}
                onClick={handleConnectBank}
              >
                🔗 Connect Bank
              </Button>
            </>
          ) : (
            <Button variant="contained" fullWidth href="/settings">
              🚀 Upgrade to ProPlus
            </Button>
          )}
        </Paper>
      ) : (
        <>
          <Box
            display="flex"
            gap={2}
            alignItems="center"
            mb={2}
            flexWrap="wrap"
          >
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                label="Month"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleString("default", {
                      month: "long",
                    })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={year}
                onChange={(e) => setYear(e.target.value)}
                label="Year"
              >
                {[2023, 2024, 2025].map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>

          <Paper sx={{ p: 2 }}>
            {loading ? (
              <Box textAlign="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Amount (€)</TableCell>
                    <TableCell align="center">Link</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTxs.map((tx) => {
                    const amountNum = parseFloat(tx.amount);
                    const isOutflow = amountNum < 0;
                    return (
                      <TableRow key={tx.transactionId}>
                        <TableCell>{formatDateWithSuffix(tx.date)}</TableCell>
                        <TableCell>
                          <Chip
                            label={
                              tx.matchedReceiptId ? "Matched" : "Unmatched"
                            }
                            color={tx.matchedReceiptId ? "success" : "default"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{tx.description}</TableCell>
                        <TableCell align="right">
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: isOutflow ? "#ef4444" : "#10b981",
                            }}
                          >
                            {Math.abs(amountNum).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {tx.matchedReceiptId ? (
                            <Chip label="Linked" color="success" size="small" />
                          ) : (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<LinkIcon />}
                              onClick={() => {}}
                            >
                              Match
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Paper>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
